import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, KPITile, DataTable, StatusPill } from "../components/Common";
import { FileText, Download, Send, Layers, PlayCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function PublishingCentre() {
  const [students, setStudents] = useState([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [classes, setClasses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState("");
  const [scope, setScope] = useState("class");
  const [classId, setClassId] = useState("");
  const [yearGroup, setYearGroup] = useState("");
  const [busy, setBusy] = useState(false);

  const loadJobs = () => api.listJobs().then(({ data }) => setJobs(data.jobs || []));

  useEffect(() => {
    api.students().then((res) => {
      setStudents(res.data);
      setStudentsTotal(res.total ?? res.data.length);
    });
    api.classes().then(({ data }) => setClasses(data));
    loadJobs();
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, []);

  const years = [...new Set(classes.map((c) => c.year_group))].sort();

  const publishOne = async () => {
    if (!selected) return toast.error("Select a student");
    setBusy(true);
    try {
      const { data } = await api.publishStudent(selected);
      toast.success(`Published v${data.version} · ${(data.size_bytes / 1024).toFixed(0)} KB`);
    } catch { toast.error("Publish failed"); }
    setBusy(false);
  };

  const downloadPdf = () => {
    if (!selected) return toast.error("Select a student");
    window.open(api.publishingStudentPdf(selected), "_blank");
  };

  const startBatch = async () => {
    setBusy(true);
    try {
      const payload = { scope };
      if (scope === "class") payload.class_id = classId;
      if (scope === "year_group") payload.year_group = yearGroup;
      const { data } = await api.startBatch(payload);
      toast.success(`Batch queued · Job ${data.id.slice(0, 8)}`);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    }
    setBusy(false);
  };

  const activeJobs = jobs.filter((j) => j.status === "processing" || j.status === "queued").length;
  const completed = jobs.filter((j) => j.status === "completed" || j.status === "completed_with_errors").length;

  return (
    <div data-testid="publishing-page">
      <PageHeader
        kicker="Executive Publishing Centre"
        title="Report Publishing & Distribution"
        subtitle="Single Education360 hub for producing every school document — publication-quality PDF, HTML, archive, batch runs, and Executive Board Packs."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPITile tone="dark" testId="pub-kpi-total" label="Documents in Archive" value={students.length > 0 ? "—" : 0} sub="see archive" />
        <KPITile testId="pub-kpi-active" label="Active Jobs" value={activeJobs} accent={activeJobs > 0 ? "processing" : "idle"} />
        <KPITile testId="pub-kpi-completed" label="Completed Runs" value={completed} />
        <KPITile tone="dark" testId="pub-kpi-students" label="Students Available" value={studentsTotal} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Publish a single report" subtitle="Render, preview or archive a full executive Student Report.">
          <div className="hl-card p-6 space-y-4">
            <div>
              <label className="overline block mb-1.5">Student</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="pub-student-select"
              >
                <option value="">— Select a student —</option>
                {students.map((s) => {
                  const optionLabel = `${s.first_name} ${s.last_name} · ${s.year_group} · ${s.house}`;
                  return (
                    <option key={s.id} value={s.id} label={optionLabel}>{optionLabel}</option>
                  );
                })}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={downloadPdf}
                disabled={!selected}
                className="hl-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-40"
                data-testid="pub-download-pdf"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button
                onClick={publishOne}
                disabled={!selected || busy}
                className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-40"
                data-testid="pub-publish-single"
              >
                <Send className="w-4 h-4" /> Publish to Archive
              </button>
            </div>
            <div className="text-xs text-slate-500">
              PDF generation uses the same premium executive template as the on-screen report, rendered server-side at 300 DPI with embedded fonts, QR verification and school seal.
            </div>
          </div>
        </Section>

        <Section title="Batch publish" subtitle="Render every student in a class, year group or house in one run.">
          <div className="hl-card p-6 space-y-4">
            <div>
              <label className="overline block mb-1.5">Scope</label>
              <div className="flex gap-2 flex-wrap">
                {["class", "year_group", "house", "all"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setScope(v)}
                    data-testid={`pub-scope-${v}`}
                    className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest border ${scope === v ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white text-slate-600 border-slate-200 hover:border-[color:var(--hl-orange)]"}`}
                  >
                    {v.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            {scope === "class" && (
              <div>
                <label className="overline block mb-1.5">Class</label>
                <select value={classId} onChange={(e) => setClassId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="pub-class-select">
                  <option value="">— select —</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {scope === "year_group" && (
              <div>
                <label className="overline block mb-1.5">Year Group</label>
                <select value={yearGroup} onChange={(e) => setYearGroup(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="pub-year-select">
                  <option value="">— select —</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            <button
              onClick={startBatch}
              disabled={busy}
              className="hl-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-40"
              data-testid="pub-batch-start"
            >
              <PlayCircle className="w-4 h-4" /> Start Batch Run
            </button>
            <div className="text-xs text-slate-500">
              Jobs run in the background. Progress updates every few seconds. Every generated report is stored in the archive with version history.
            </div>
          </div>
        </Section>
      </div>

      <Section title="Publishing jobs" testId="pub-jobs">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={loadJobs} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <DataTable
          columns={[
            { key: "kind", label: "Kind", render: (r) => <span className="capitalize">{(r.kind || "").replace(/_/g, " ")}</span> },
            { key: "scope", label: "Scope", render: (r) => `${r.scope}${r.filters?.class_id ? ` · class` : r.filters?.year_group ? ` · ${r.filters.year_group}` : r.filters?.house ? ` · ${r.filters.house}` : ""}` },
            { key: "status", label: "Status", render: (r) => (
              <StatusPill status={r.status === "processing" ? "active" : r.status === "queued" ? "info" : r.status === "completed" ? "resolved" : r.status === "completed_with_errors" ? "warning" : "danger"} />
            ) },
            { key: "progress", label: "Progress", render: (r) => `${r.processed || 0} / ${r.total || 0}` },
            { key: "archived", label: "Archived", render: (r) => (r.archived_ids || []).length },
            { key: "created_at", label: "Started", render: (r) => (r.created_at || "").slice(11, 19) },
          ]}
          rows={jobs}
          empty="No publishing runs yet."
        />
      </Section>
    </div>
  );
}
